import { useState } from "react";
import { useImport } from '@core/api/useImport';
import { toast } from "sonner";
import { ImportFile } from '@shared/components/ImportFile';
import { useTranslation } from "react-i18next";
import { EnhancedImportFile } from "@shared/components/EnhancedImportFile";
import { Alert, AlertDescription } from "@shared/components/ui/alert";
import { Badge } from "@shared/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "@shared/components/ui/card";
import { ScrollArea } from "@shared/components/ui/scroll-area";
import { CheckCircle, XCircle, AlertTriangle, Info } from "lucide-react";

const template = {
  name: "import-products.xlsx",
  url: "/files/import-products.xlsx",
};

const ImportProducts = () => {
  const { t } = useTranslation();
  const { importProducts: importFile, importProductsLoading } = useImport();
  const [file, setFile] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [showDetails, setShowDetails] = useState(false);
  
  let toastId = null;
  if (importProductsLoading) {
    toastId = toast.loading(t("toast.uploading"));
  }

  const handleFileChange = (event) => {
    setFile(event.target.files[0]);
    setImportResult(null); // Reset previous results
  };

  const handleUpload = async () => {
    if (!file) {
      return null;
    }

    const formData = new FormData();
    formData.append("file", file);
    
    importFile(formData, {
      onSuccess: (response) => {
        toast.dismiss(toastId);
        setImportResult(response.data);
        
        const { summary } = response.data;
        const message = `Import completed: ${summary.successful} successful, ${summary.failed} failed, ${summary.duplicates} duplicates`;
        
        if (summary.failed > 0 || summary.duplicates > 0) {
          toast.warning(message);
        } else {
          toast.success(message);
        }
      },
      onError: (error) => {
        toast.dismiss(toastId);
        toast.error(error.response?.data?.message || "Import failed");
      },
    });
  };

  const renderSummary = () => {
    if (!importResult) return null;
    
    const { summary } = importResult;
    
    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Info className="h-5 w-5" />
            Import Summary
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-blue-600">{summary.total}</div>
              <div className="text-sm text-gray-600">Total Rows</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-green-600">{summary.successful}</div>
              <div className="text-sm text-gray-600">Successful</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-red-600">{summary.failed}</div>
              <div className="text-sm text-gray-600">Failed</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-yellow-600">{summary.duplicates}</div>
              <div className="text-sm text-gray-600">Duplicates</div>
            </div>
            <div className="text-center">
              <div className="text-2xl font-bold text-gray-600">{summary.skipped}</div>
              <div className="text-sm text-gray-600">Skipped</div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  const renderDetails = () => {
    if (!importResult || !showDetails) return null;
    
    const { details } = importResult;
    
    return (
      <Card className="mb-4">
        <CardHeader>
          <CardTitle>Import Details</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            {details.invalidItems && details.invalidItems.length > 0 && (
              <div>
                <h4 className="font-semibold text-red-600 mb-2 flex items-center gap-2">
                  <XCircle className="h-4 w-4" />
                  Failed Items ({details.invalidItems.length})
                </h4>
                <ScrollArea className="h-32">
                  <div className="space-y-2">
                    {details.invalidItems.map((item, index) => (
                      <Alert key={index} variant="destructive">
                        <AlertDescription>
                          <div className="font-medium">Row {item.rowNumber}</div>
                          <div className="text-sm">{item.errors.join(", ")}</div>
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
            
            {details.duplicates && details.duplicates.length > 0 && (
              <div>
                <h4 className="font-semibold text-yellow-600 mb-2 flex items-center gap-2">
                  <AlertTriangle className="h-4 w-4" />
                  Duplicate Items ({details.duplicates.length})
                </h4>
                <ScrollArea className="h-32">
                  <div className="space-y-2">
                    {details.duplicates.map((item, index) => (
                      <Alert key={index} variant="default">
                        <AlertDescription>
                          <div className="font-medium">Row {item.rowNumber}: {item.data.name}</div>
                          <div className="text-sm">{item.reason}</div>
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
            
            {details.skippedRows && details.skippedRows.length > 0 && (
              <div>
                <h4 className="font-semibold text-gray-600 mb-2 flex items-center gap-2">
                  <Info className="h-4 w-4" />
                  Skipped Rows ({details.skippedRows.length})
                </h4>
                <ScrollArea className="h-32">
                  <div className="space-y-2">
                    {details.skippedRows.map((item, index) => (
                      <Alert key={index} variant="secondary">
                        <AlertDescription>
                          <div className="font-medium">Row {item.rowNumber}</div>
                          <div className="text-sm">{item.reason}</div>
                        </AlertDescription>
                      </Alert>
                    ))}
                  </div>
                </ScrollArea>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div>
      <EnhancedImportFile
        template={template}
        handleFileChange={handleFileChange}
        handleUpload={handleUpload}
      />
      
      {importResult && (
        <div className="mt-6">
          {renderSummary()}
          
          {importResult.details && (
            <div className="flex justify-center mb-4">
              <button
                onClick={() => setShowDetails(!showDetails)}
                className="text-blue-600 hover:text-blue-800 text-sm font-medium"
              >
                {showDetails ? "Hide Details" : "Show Details"}
              </button>
            </div>
          )}
          
          {renderDetails()}
        </div>
      )}
    </div>
  );
};

export default ImportProducts;
